








import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js';
import _ from 'lodash';
import { utilService } from '../util-service';
import chartUtil from './util.service';
import SwChartChartJsMixin from './sw-chart-chart-js.mixin.vue';
import { DonutCenterLabelsFormatter } from './types';

const defaultCenterLabelsFormatter: DonutCenterLabelsFormatter = (labels) => {
  return [
    `${labels.value}`,
    `${labels.label}`,
    `${!labels.total ? 0 : Math.round(100 * labels.value / labels.total)}%`,
  ];
};

@Component
export default class SwChartDonut extends mixins(SwChartChartJsMixin) {
  @Prop({ type: Array, default () { return []; } })
  chartData: Chart.ChartDataSets[];

  @Prop({ type: Array })
  highlightSlice: [number, number] | undefined; // [datasetIndex, dataIndex]

  @Prop(Array)
  labels: string[];

  @Prop({ type: Function, default: defaultCenterLabelsFormatter })
  centerLabelsFormatter: DonutCenterLabelsFormatter;

  selected = -1;
  datasetIndex = -1;

  get chartOptions(): Chart.ChartOptions {
    const options: Chart.ChartOptions = {
      cutoutPercentage: 65,
      legend: {
        display: false,
      },
      onHover: (evt, elems) => {// tslint:disable-line
        if (this.chartInstance) {
          const evtPoint = elems as any[];
          if (evtPoint && evtPoint.length > 0) {
            this.datasetIndex = evtPoint[0]._datasetIndex;
            this.selected = evtPoint[0]._index;
          } else {
            this.selected = -1;
            this.datasetIndex = -1;
          }
        }
      },
      plugins: {
        doughnutlabel: {
          labels: this.centerLabels.map((text: string) => {
            return {
              text,
              font: {
                size: '16',
              },
              color: chartUtil.chartTextColor(this.theme),
            };
          }),
        },
      },
      tooltips: {
        enabled: false,
      },
    };
    return _.merge(options, this.options);
  }

  get dataSetOption() {
    return {
      borderColor: chartUtil.elementBorderColor(this.theme),
      hoverBorderColor: utilService.colorConst.selected,
      backgroundColor: utilService.colorConst.chartPrimaryPalette,
    };
  }

  get centerLabels(): string[] {
    if (
      this.datasetIndex > -1 &&
      this.selected > -1 &&
      this.chartData[this.datasetIndex] &&
      this.chartData[this.datasetIndex].data &&
      this.chartData[this.datasetIndex].data!.length > this.selected
    ) {
      const data = this.chartData[this.datasetIndex].data as number[];
      const sum = data.reduce((p: number, c: number) => { return p + c; }, 0);
      const labels = this.centerLabelsFormatter({
        value: data[this.selected],
        label: this.labels[this.selected],
        total: sum,
      });
      return labels;
    }
    return [''];
  }

  get normalizedDatasets() {
    return this.chartData.map((dataset: Chart.ChartDataSets) => {
      return Object.assign({}, dataset, this.dataSetOption);
    });
  }

  private updateData() {
    if (this.chartInstance) {
      chartUtil.updateChartRegularData(this.chartInstance, this.labels, this.normalizedDatasets);
    }
  }

  @Watch('labels')
  @Watch('theme')
  @Watch('normalizedDatasets')
  @Watch('chartOptions')
  private onConfigChanged() {
    if (this.chartInstance) {
      this.updateData();
      (this.chartInstance as any).options = this.chartOptions;
      this.chartInstance.update();

      if (Array.isArray(this.highlightSlice) && this.selected === this.highlightSlice[1] && this.datasetIndex === this.highlightSlice[0]) {
        this.highlightDefault();
      } else if (this.selected < 0 && this.datasetIndex < 0) {
        this.debouncedHighlightDefault();
      } else {
        this.debouncedHighlightDefault.cancel();
      }
    }
  }

  @Watch('highlightSlice')
  private onHighlightSliceChange() {
    this.selected = -1;
    this.datasetIndex = -1;
    this.dbDebouncedHighlightDefault();
  }

  private highlightDefault() {
    if (Array.isArray(this.highlightSlice) && this.chartInstance) {
      if (
        this.selected >= 0 && this.datasetIndex >= 0 &&
        (this.selected !== this.highlightSlice[1] || this.datasetIndex !== this.highlightSlice[0])
      ) { return; }
      const datasetMeta = this.chartInstance.getDatasetMeta(this.highlightSlice[0]);
      if (!datasetMeta) { return; }
      const pointMeta = datasetMeta.data[this.highlightSlice[1]];
      if (!pointMeta) { return; }

      const point: { x: number; y: number; } = (pointMeta as any).getCenterPoint();
      const rect = this.chartInstance.canvas!.getBoundingClientRect();
      this.chartInstance.canvas!.dispatchEvent(
        new MouseEvent('mousemove', {
          clientX: rect.left + point.x,
          clientY: rect.top + point.y,
        }),
      );
    }
  }
  private get debouncedHighlightDefault() {
    return  _.debounce(this.highlightDefault, 400);
  }
  private get dbDebouncedHighlightDefault() {
    return  _.debounce(this.debouncedHighlightDefault, 400);
  }

  private created() {
    this.myCssPrefix = 'sw-chart-donut';
  }

  private mounted() {
    this.initChart({
      type: 'doughnut',
      data: {
        labels: this.labels,
        datasets: this.normalizedDatasets,
        // chartData should contain only data series. but mixined with const color.
      },
      options: this.chartOptions,
    });

    setTimeout(this.highlightDefault, 500);
  }

  protected beforeDestroy() {
    this.dbDebouncedHighlightDefault.cancel();
    this.debouncedHighlightDefault.cancel();
  }
}
